import { useRouteRef } from '@backstage/frontend-plugin-api';
import { EntityRefLink, entityRouteRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef, type Entity } from '@backstage/catalog-model';
import {
  Flex,
  Text,
  Table,
  useTable,
  CellText,
  Cell,
  type ColumnConfig,
  type SortDescriptor,
} from '@backstage/ui';
import { normalizeOwnerEntityRef } from '@internal/backstage-plugin-pulse-common';
import { type TrackDetailsData, type TrackResultRow } from '../../hooks';
import { buildEntityPulseHref } from '../entity-content';
import { CompliancePercentageBar } from './compliance-indicators';
import { paginationOptions, sortCollator } from '../config';

type EntityRow = TrackResultRow & { id: string };

const entitySpecString = (
  entity: Entity | undefined,
  key: 'system' | 'type' | 'lifecycle' | 'owner',
): string => {
  const value = entity?.spec?.[key];
  return typeof value === 'string' ? value : '';
};

const entityKind = (item: EntityRow): string =>
  item.entity?.kind ?? item.entityRef.split(':')[0] ?? '';

const entityName = (item: EntityRow): string =>
  item.entity?.metadata?.title ??
  item.entityRef.split('/').pop() ??
  item.entityRef;

const getEntitySortableValue = (item: EntityRow, column: string): string => {
  switch (column) {
    case 'entity':
      return entityName(item);
    case 'compliance':
      return String(item.overallPercentage);
    case 'system':
      return entitySpecString(item.entity, 'system');
    case 'type':
      return entitySpecString(item.entity, 'type');
    case 'lifecycle':
      return entitySpecString(item.entity, 'lifecycle');
    case 'kind':
      return entityKind(item);
    case 'owner':
      return entitySpecString(item.entity, 'owner');
    default:
      return '';
  }
};

const sortEntityRows = (
  data: EntityRow[],
  sort: SortDescriptor,
): EntityRow[] => {
  const direction = sort.direction === 'descending' ? -1 : 1;
  const sortColumn = String(sort.column);

  return [...data].sort((left, right) => {
    const comparison = sortCollator.compare(
      getEntitySortableValue(left, sortColumn).trim(),
      getEntitySortableValue(right, sortColumn).trim(),
    );

    if (comparison !== 0) {
      return comparison * direction;
    }

    return sortCollator.compare(left.id, right.id);
  });
};

const createEntityColumns = (
  entityRoute:
    | ((params: { kind: string; namespace: string; name: string }) => string)
    | undefined,
  trackId?: string,
): ColumnConfig<EntityRow>[] => [
  {
    id: 'entity',
    label: 'Name',
    isRowHeader: true,
    defaultWidth: '28fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={entityName(item)}
        href={
          entityRoute
            ? buildEntityPulseHref(
                entityRoute(parseEntityRef(item.entityRef)),
                {
                  trackId,
                },
              )
            : undefined
        }
      />
    ),
  },
  {
    id: 'compliance',
    label: 'Compliance',
    defaultWidth: '24fr',
    isSortable: true,
    cell: item => (
      <Cell>
        <CompliancePercentageBar percentage={item.overallPercentage} />
      </Cell>
    ),
  },
  {
    id: 'system',
    label: 'System',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={entitySpecString(item.entity, 'system') || '-'}
        color="secondary"
      />
    ),
  },
  {
    id: 'type',
    label: 'Type',
    defaultWidth: '13fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={entitySpecString(item.entity, 'type') || '-'}
        color="secondary"
      />
    ),
  },
  {
    id: 'lifecycle',
    label: 'Lifecycle',
    defaultWidth: '13fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={entitySpecString(item.entity, 'lifecycle') || '-'}
        color="secondary"
      />
    ),
  },
  {
    id: 'kind',
    label: 'Kind',
    defaultWidth: '12fr',
    isSortable: true,
    cell: item => <CellText title={entityKind(item)} color="secondary" />,
  },
  {
    id: 'owner',
    label: 'Owner',
    defaultWidth: '18fr',
    isSortable: true,
    cell: item => {
      const owner = entitySpecString(item.entity, 'owner');
      const ownerEntityRef = owner ? normalizeOwnerEntityRef(owner) : undefined;

      if (ownerEntityRef) {
        return (
          <Cell>
            <EntityRefLink entityRef={ownerEntityRef} underline="none" />
          </Cell>
        );
      }

      return <CellText title={owner || '-'} color="secondary" />;
    },
  },
];

/**
 * "Entities" tab: a sortable, paginated table of every entity evaluated by the
 * track, showing overall compliance and catalog metadata.
 */
export const TrackEntitiesTable = ({
  details,
}: {
  details: TrackDetailsData;
}) => {
  const entityRoute = useRouteRef(entityRouteRef);
  const columns = createEntityColumns(entityRoute, details.track.id);

  const rows: EntityRow[] = details.results.map((result, index) => ({
    ...result,
    id: `${result.entityRef}-${index}`,
  }));

  const { tableProps } = useTable<EntityRow, string[]>({
    mode: 'complete',
    data: rows,
    initialSort: { column: 'entity', direction: 'ascending' },
    sortFn: sortEntityRows,
    paginationOptions,
  });

  return (
    <Flex direction="column" gap="2">
      <Text variant="title-small" weight="bold">
        Entities ({rows.length})
      </Text>
      <Table
        columnConfig={columns}
        {...tableProps}
        emptyState="No entities evaluated"
      />
    </Flex>
  );
};
